import { useCallback, useEffect, useRef, useState } from 'react';
import { session } from './lib/api/session.js';
import { getMe, type Contributor } from './lib/api/endpoints.js';
import { computeCreditRules, totalCredits, useObservationDigest } from './lib/standing.js';
import { SignInPage } from './features/auth/SignInPage.js';
import { ConsentGate } from './features/consent/ConsentGate.js';
import { ContributePage } from './features/contribute/ContributePage.js';
import { SubjectsPage } from './features/subjects/SubjectsPage.js';
import { TimelinePage } from './features/timeline/TimelinePage.js';
import { StandingPage } from './features/standing/StandingPage.js';
import { HelpPage } from './features/help/HelpPage.js';
import { ContactPage } from './features/contact/ContactPage.js';
import { ErrorNotice } from './components/ErrorNotice.js';
import { OfflineBanner } from './components/OfflineBanner.js';

const BRAND_NAME = import.meta.env.VITE_BRAND_NAME ?? 'VoxFaunix';

/**
 * Hash routing, deliberately.
 *
 * The app is a static bundle and will be served from GitLab Pages alongside the
 * marketing site, possibly on a project subpath. Hash routes need no server
 * rewrite rules and cannot 404 on a deep link, which removes a class of
 * deployment bug for a client that has no routing requirements beyond four
 * screens. Swap for the History API when there is a reason.
 */
type Route =
  | { name: 'contribute' }
  | { name: 'animals' }
  | { name: 'timeline'; subjectId?: string }
  | { name: 'record' }
  | { name: 'help' }
  | { name: 'contact' };

function parseRoute(hash: string): Route {
  const path = hash.replace(/^#\/?/, '');
  const [head, tail] = path.split('/');

  if (head === 'animals') return { name: 'animals' };
  if (head === 'timeline') return tail ? { name: 'timeline', subjectId: tail } : { name: 'timeline' };
  if (head === 'record') return { name: 'record' };
  if (head === 'help') return { name: 'help' };
  if (head === 'contact') return { name: 'contact' };
  return { name: 'contribute' };
}

export function App() {
  const [signedIn, setSignedIn] = useState(session.hasSession());
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.hash));
  const [contributor, setContributor] = useState<Contributor | null>(null);
  // Null until the server has said what the licence is — never a guessed default.
  // It is read from the API (via the consent gate) rather than hardcoded, so a
  // policy change needs no client release, and it is written into every submitted
  // record, so a wrong one is worse than none: the contribute page holds Submit
  // back until this has loaded.
  const [licence, setLicence] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);

  // Five nav items no longer fit on one line below ~720px — rather than let
  // them wrap mid-label (a "Field" / "record" split reads as broken, not
  // responsive), the nav collapses into a toggled panel there instead.
  const [navOpen, setNavOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => session.onChange(setSignedIn), []);

  // Contributors are provisioned lazily on the first authenticated request,
  // so this call both fetches the profile and creates it if this is a first
  // sign-in. There is no separate signup step.
  const loadProfile = useCallback(() => {
    setLoadError(null);
    getMe()
      .then(setContributor)
      .catch(setLoadError);
  }, []);


  useEffect(() => {
    const onHashChange = () => setRoute(parseRoute(window.location.hash));
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // A route change means a nav link was just followed — close the panel so
  // it doesn't linger open over the page it navigated to.
  useEffect(() => setNavOpen(false), [route]);

  useEffect(() => {
    if (!navOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    const onClickOutside = (e: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) setNavOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onClickOutside);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, [navOpen]);

  // Every recording promises credits ("+10 credits when it lands") but, until
  // now, the only place a contributor could see the total they'd built up was
  // a page buried under the ambiguously-labelled "Field record" nav item.
  // Surfacing it in the header — present on every screen — closes that loop.
  // useObservationDigest() is shared/cached across the whole app (see
  // lib/standing.ts), so this costs nothing extra: Contribute, Timeline and
  // Field record already trigger the same fetch.
  const { digest } = useObservationDigest();
  const credits = digest ? totalCredits(computeCreditRules(digest)) : null;

  useEffect(() => {
    if (!signedIn) {
      setContributor(null);
      return;
    }

    loadProfile();
  }, [signedIn, loadProfile]);

  if (!signedIn) {
    return (
      <div className="app">
        <main className="main main--center">
          <SignInPage />
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="topbar" ref={headerRef}>
        <a className="wordmark" href="#/contribute">
          {BRAND_NAME}
        </a>

        <div className="topbar-actions">
          {/* Present on every screen, collapsed nav or not — a running total
              is exactly the kind of thing that shouldn't disappear behind a
              hamburger on narrow viewports. */}
          {credits !== null && (
            <a className="credits-badge" href="#/record" title="Field credits — see the full breakdown">
              <strong>{credits}</strong> credits
            </a>
          )}

          <button
            type="button"
            className="nav-toggle"
            aria-expanded={navOpen}
            aria-controls="topnav"
            onClick={() => setNavOpen((open) => !open)}
          >
            <span className="nav-toggle-bars" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
            <span className="sr-only">Menu</span>
          </button>
          <nav className="topnav" id="topnav" data-open={navOpen}>
            <a href="#/contribute" aria-current={route.name === 'contribute' ? 'page' : undefined}>
              Contribute
            </a>
            <a href="#/timeline" aria-current={route.name === 'timeline' ? 'page' : undefined}>
              Timeline
            </a>
            <a href="#/record" aria-current={route.name === 'record' ? 'page' : undefined}>
              Field record
            </a>
            <a href="#/animals" aria-current={route.name === 'animals' ? 'page' : undefined}>
              Animals
            </a>
            <a href="#/help" aria-current={route.name === 'help' ? 'page' : undefined}>
              Help
            </a>
            <a href="#/contact" aria-current={route.name === 'contact' ? 'page' : undefined}>
              Contact
            </a>
            <button type="button" onClick={() => session.clear()}>
              Sign out
            </button>
          </nav>
        </div>
      </header>

      {/* Dims the page behind the open mobile menu so it reads as a panel
          floating above the content, not as part of it — see nav-toggle's
          collapsed state in app.css. A sibling of `.topbar`, not a child of
          it, so the existing outside-click handler above (which checks
          headerRef.current.contains) already closes the menu when this is
          clicked, with no extra wiring. */}
      {navOpen && <div className="nav-backdrop" aria-hidden="true" />}

      <OfflineBanner />

      <main className="main">
        {loadError !== null && !contributor && (
          <ErrorNotice
            error={loadError}
            title="We couldn't load your account."
            onRetry={loadProfile}
          />
        )}

        {route.name === 'help' ? (
          <HelpPage />
        ) : route.name === 'contact' ? (
          <ContactPage />
        ) : !contributor ? (
          // A failed load already has its notice (with Retry) above; a spinner
          // under it would read as "still trying" when nothing is.
          loadError !== null ? null : <p className="meta">Loading…</p>
        ) : route.name === 'animals' ? (
          <SubjectsPage />
        ) : route.name === 'timeline' ? (
          <TimelinePage {...(route.subjectId ? { subjectId: route.subjectId } : {})} />
        ) : route.name === 'record' ? (
          <StandingPage />
        ) : (
          // The consent gate wraps only the contribution flow. Someone can look
          // at their own timeline and manage their animals without having
          // accepted the corpus terms — the terms govern contributing data, and
          // gating everything behind them would be a wall in front of the app.
          <ConsentGate onLicence={setLicence}>
            <ContributePage
              contributor={contributor}
              licence={licence}
              onContributed={(id) => {
                window.location.hash = '#/timeline';
                 
                console.info(`[${BRAND_NAME.toLowerCase()}] contributed ${id}`);
              }}
            />
          </ConsentGate>
        )}
      </main>
    </div>
  );
}
